import { Component, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { AuthService } from '@uigy/shared/services/auth.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { select, Store } from '@ngrx/store';
import { State } from '@uigy/store/appState';
import { ActionsFlyout, FlyoutSettings } from '@uigy/shared/models/interfaces';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PopupService } from '@uigy/shared/services/popup.service';
import { Router } from '@angular/router';
import { GetUserInfoAction } from '@uigy/store/actions/config.actions';
import { selectUserName } from '@uigy/store';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { IemService } from '@uigy/shared/services/iem.service';
import { ActiveQueriesService } from '@uigy/modules/active-queries/services/active-queries.service';
import { map, take, tap } from 'rxjs/operators';
import { Capability } from '@uigy/generated/graphql';
import { CapabilitiesService } from '@uigy/shared/capabilities/capabilities.service';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { ConfigService } from '@uigy/shared/services/config.service';
import { PendoService } from '@uigy/shared/services/pendo.service';
import { DarkModeService } from '@uigy/shared/services/dark-mode.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit {
  protected readonly IconsEnum = IconsEnum;
  protected readonly routes = RoutesStatesNamesEnum;
  public readonly AppIdsEnum = AppIdsEnum;
  public clockFormat: DatesFormatsEnum = DatesFormatsEnum.hourMinutes;
  public fullDateFormat: DatesFormatsEnum = DatesFormatsEnum.fullDate;
  public headerFlyoutSettings!: FlyoutSettings<ActionsFlyout>;
  private isShowNavigation: boolean = true;
  public activeQueriesIsReady = false;
  private activeQueriesTooltipTexts = this.languageService.texts.queries.active_queries.global_config.enabled.tooltips;
  public passiveConsumptionTooltipTexts: string = this.languageService.texts.onboarding.passive_monitoring.header.state.disabled;

  public passiveConsumptionValue$ = this.configService.configResponse$.pipe(
    map(configResponse => !!configResponse?.PassiveConsumption?.Value),
    tap(isEnabled => {
      this.passiveConsumptionTooltipTexts = isEnabled
        ? this.languageService.texts.onboarding.passive_monitoring.header.state.enabled
        : this.languageService.texts.onboarding.passive_monitoring.header.state.disabled;
    })
  );

  public colorThemeClass = signal<string>('');
  public pendoTooltipTexts: string = this.languageService.texts.pendo.icon.tooltip.disabled;
  public isDisabledPendo = signal<boolean>(true);

  @Input() navigationElement!: HTMLElement;
  @Input() contentElement!: HTMLElement;

  constructor(store: Store<State>,
              public activeQueriesService: ActiveQueriesService,
              public iemService: IemService,
              private languageService: LanguageService,
              private router: Router,
              private popupService: PopupService,
              private authService: AuthService,
              private capabilitiesService: CapabilitiesService,
              private configService: ConfigService,
              private pendoService: PendoService,
              private darkModeService: DarkModeService,
              private destroyRef: DestroyRef,
  ) {
    store.dispatch(new GetUserInfoAction());

    store.pipe(select(selectUserName))
      .subscribe((username: string | null) => {
        this.headerFlyoutSettings = {
          appId: AppIdsEnum.header_flyout,
          labelKey: 'label',
          valueKey: 'execute',
          items: [
            {
              id: AppIdsEnum.header_user_about,
              label: this.languageService.texts.about.buttons.about,
              execute: this.about,
            },
            this.iemService.isIcpViewInEm() ? {
              id: AppIdsEnum.header_icp_view_logout,
              label: this.languageService.texts.icps.return_to_em,
              execute: this.redirectToEm,
            } : {
              id: AppIdsEnum.header_user_logout,
              label: this.languageService.texts.about.buttons.logout,
              execute: this.logout,
            },
          ],
          selected: { label: this.languageService.texts.about.buttons.logout, execute: this.logout },
          disable: false,
          title: <string>username,
        };
      });

    if (!iemService.isIem) {
      this.activeQueriesService.getIsGlobalPauseOn().pipe(
        tap(() => this.activeQueriesIsReady = true),
        take(1)
      ).subscribe();
    } else {
      this.activeQueriesIsReady = true;
    }
  }

  public ngOnInit(): void {
    this.darkModeService.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(darkMode => {
        this.colorThemeClass.set(darkMode ? 'dark-mode' : '');
      });

    this.pendoService.pendoActive$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(active => {
        this.isDisabledPendo.set(!active);
        this.pendoTooltipTexts = this.isDisabledPendo() ? this.languageService.texts.pendo.icon.tooltip.disabled : '';
      });
  }

  public onHeaderFlyoutChange(flyoutData: ActionsFlyout): void {
    flyoutData.execute.bind(this)();
  }

  private redirectToEm(): void {
    window.location.href = window.location.origin;
  }

  private logout(): void {
    this.authService.logout();
  }

  private about(): void {
    this.popupService.openAboutPopup();
  }

  private setHtmlNavigationValues(displayValue: string = '', gridColumnValue: string = '', msGridValue: string = '2', msGridSpanValue: string = ''): void {
    this.navigationElement.style.display = displayValue;
    this.contentElement.style.setProperty('grid-column', gridColumnValue);
    this.contentElement.style.setProperty('-ms-grid-column', msGridValue);
    this.contentElement.style.setProperty('-ms-grid-column-span', msGridSpanValue);
    this.isShowNavigation = !this.isShowNavigation;
  }

  public toggleNavigation(): void {
    if (this.isShowNavigation) {
      this.setHtmlNavigationValues('none', '1/-1', '1', '2');
    } else {
      this.setHtmlNavigationValues();
    }
  }

  public resolveActiveQueriesIconTooltip(): string {
    const isCapableOfEditing = this.capabilitiesService.capableOf(Capability.WriteActiveQueries);

    return this.activeQueriesService.isGlobalPauseOn ?
      isCapableOfEditing ? this.activeQueriesTooltipTexts.off_admin: this.activeQueriesTooltipTexts.off :
      this.activeQueriesTooltipTexts.on;
  }
}
